/**
 * Collapsible left sidebar.
 *
 * - 220px expanded, 56px collapsed (icon-only).
 * - Width transition 180ms ease; label opacity 150ms.
 * - Collapsed state hides labels/badges via opacity:0 (NOT display:none) so
 *   layout stays stable across expand/collapse.
 * - State persisted in `localStorage` key `tc.sidebar.collapsed`.
 * - When collapsed, tooltip shows the item name on hover.
 *
 * Active state: bg #F0FDF9, text #0BA888, font-weight 500.
 * Primary: #0BA888. Font: DM Sans (loaded in public/index.html).
 *
 * NOTE: Keep this component free of large IIFEs/JSX recursion — the visual-edits
 * Babel plugin in this repo will hit "Maximum call stack size exceeded" on
 * deeply nested IIFE patterns. Extracted helpers `Section`, `Item`, `Footer`
 * exist for that reason.
 */
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, Users, FileText, Package,
  UserPlus, Receipt, IndianRupee, Wallet, Building2, BookOpen,
  CreditCard, BarChart3, LineChart, Globe, Wrench, Settings, ScanSearch,
  ChevronLeft, CalendarClock, LayoutTemplate, History, Cpu, MessageCircle, MessageSquare, Zap, Undo2, FileDown, Landmark, Wallet2, HandCoins, AlertTriangle, MapPin, ShieldAlert, PieChart, Copy,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'tc.sidebar.collapsed';
const FONT = "'DM Sans', system-ui, sans-serif";
const ACTIVE_BG = '#F0FDF9';
const PRIMARY = '#0BA888';

const Sidebar = ({ user }) => {
  const location = useLocation();
  const role = ((user && user.role) || '').toLowerCase();
  const isAdmin = role === 'admin';
  const isAccounts = role === 'accounts';
  const isHr = role === 'hr';
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [counts, setCounts] = useState({ vouchersMissing: 0 });

  // Persist collapse state.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0'); } catch { /* storage unavailable — non-critical */ }
  }, [collapsed]);

  // External toggle hook — Cmd/Ctrl+B keyboard shortcut fires this event.
  // We re-read the (already updated) localStorage value so the shortcut and
  // chevron-button stay in sync no matter who fires first.
  useEffect(() => {
    const sync = () => {
      try { setCollapsed(localStorage.getItem(STORAGE_KEY) === '1'); } catch { /* storage unavailable — non-critical */ }
    };
    window.addEventListener('tc:sidebar-toggle', sync);
    return () => window.removeEventListener('tc:sidebar-toggle', sync);
  }, []);

  // One lightweight fetch for badge counts. Re-fetch on route change keeps
  // the missing-voucher badge fresh after the user generates a voucher.
  useEffect(() => {
    let alive = true;
    axios.get(`${BACKEND_URL}/api/dashboard/sidebar-counts`)
      .then((res) => { if (alive) setCounts(res.data || {}); })
      .catch(() => { /* badge is non-critical; silent fail keeps chrome clean */ });
    return () => { alive = false; };
  }, [location.pathname]);

  const width = collapsed ? 56 : 220;
  const p = location.pathname;

  // Active matchers — flat (no nested IIFEs) to avoid Babel plugin recursion.
  const isActive = (paths) => paths.some((path) => {
    if (path === '/') return p === '/' || p === '/dashboard';
    return p === path || p.startsWith(path + '/');
  });

  return (
    <TooltipProvider delayDuration={200}>
      <aside
        data-testid="app-sidebar"
        data-collapsed={collapsed ? 'true' : 'false'}
        className="hidden md:flex sticky top-0 h-screen bg-white border-r border-slate-200 z-40 shrink-0 flex-col"
        style={{ width, transition: 'width 180ms ease', fontFamily: FONT }}
      >
        {/* Toggle button (chevron circle, top-right) */}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          data-testid="sidebar-toggle"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="absolute -right-3 top-5 h-6 w-6 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center hover:bg-slate-50 z-50"
        >
          <ChevronLeft
            className="h-3.5 w-3.5 text-slate-500"
            style={{ transition: 'transform 180ms ease', transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </button>

        {/* Nav body — scrolls vertically when the items don't fit a short
            viewport so every tab (incl. Settings/Admin) stays reachable.
            Footer below stays pinned via the flex column layout. */}
        <nav className="flex-1 px-2 pt-5 pb-3 space-y-4 overflow-y-auto overflow-x-hidden sidebar-scroll">
          {isAccounts ? (
            <>
              <Section collapsed={collapsed}>
                <Item collapsed={collapsed} icon={IndianRupee} label="Dashboard" to="/accounts" active={isActive(['/accounts'])} testid="sidebar-accounts-dashboard" />
              </Section>
              <Section collapsed={collapsed} label="CRM">
                <Item collapsed={collapsed} icon={Users} label="Travellers" to="/clients" active={isActive(['/clients'])} testid="sidebar-travellers" />
                <Item collapsed={collapsed} icon={FileText} label="Trips" to="/quotes" active={isActive(['/quotes'])} testid="sidebar-trips" />
                <Item collapsed={collapsed} icon={MessageCircle} label="WhatsApp" to="/whatsapp" active={p === '/whatsapp'} testid="sidebar-whatsapp" />
                <Item collapsed={collapsed} icon={MapPin} label="Destinations" to="/destinations" active={isActive(['/destinations'])} testid="sidebar-destinations" />
              </Section>
              <Section collapsed={collapsed} label="Finance">
                <Item collapsed={collapsed} icon={Receipt} label="Vouchers" to="/vouchers" active={isActive(['/vouchers'])} testid="sidebar-vouchers" />
                <Item collapsed={collapsed} icon={FileText} label="Invoices" to="/invoices" active={isActive(['/invoices'])} testid="sidebar-invoices" />
                <Item collapsed={collapsed} icon={IndianRupee} label="Receipts" to="/finance/receipts" active={isActive(['/finance/receipts'])} testid="sidebar-receipts" />
                <Item collapsed={collapsed} icon={BookOpen} label="Ledger" to="/ledger" active={isActive(['/ledger'])} testid="sidebar-ledger" />
                <Item collapsed={collapsed} icon={CreditCard} label="Payment Links" to="/payment-links" active={isActive(['/payment-links'])} testid="sidebar-payment-links" />
                <Item collapsed={collapsed} icon={Building2} label="Vendors" to="/vendors" active={isActive(['/vendors'])} testid="sidebar-vendors" />
                <Item collapsed={collapsed} icon={Undo2} label="Credit Notes" to="/finance/credit-notes" active={isActive(['/finance/credit-notes'])} testid="sidebar-credit-notes" />
                <Item collapsed={collapsed} icon={HandCoins} label="Reimbursements" to="/finance/reimbursements" active={isActive(['/finance/reimbursements'])} testid="sidebar-reimbursements" />
              </Section>
              <Section collapsed={collapsed} label="Reports">
                <Item collapsed={collapsed} icon={LineChart} label="Analytics" to="/analytics" active={isActive(['/analytics'])} testid="sidebar-analytics" />
                <Item collapsed={collapsed} icon={BarChart3} label="GST Report" to="/reports/gst" active={p === '/reports/gst'} testid="sidebar-gst-report" />
                <Item collapsed={collapsed} icon={BarChart3} label="TCS Report" to="/reports/tcs" active={p === '/reports/tcs'} testid="sidebar-tcs-report" />
                <Item collapsed={collapsed} icon={History} label="Historical Tax" to="/accounting/gst-tcs/historical" active={p === '/accounting/gst-tcs/historical'} testid="sidebar-historical-tax" />
                <Item collapsed={collapsed} icon={ScanSearch} label="Reconciliation" to="/reports/reconciliation" active={p === '/reports/reconciliation'} testid="sidebar-reconciliation-report" />
                <Item collapsed={collapsed} icon={PieChart} label="Reconstructed Accounts" to="/reports/reconstructed-accounts" active={p === '/reports/reconstructed-accounts'} testid="sidebar-reconstructed-accounts" />
                <Item collapsed={collapsed} icon={Globe} label="FX Reporting" to="/reports/fx" active={p === '/reports/fx'} testid="sidebar-fx-report" />
                <Item collapsed={collapsed} icon={FileDown} label="Tally Export" to="/reports/tally-export" active={p === '/reports/tally-export'} testid="sidebar-tally-export" />
                <Item collapsed={collapsed} icon={Landmark} label="Bank / Cash Accounts" to="/accounting/bank-accounts" active={p === '/accounting/bank-accounts'} testid="sidebar-bank-accounts" />
                <Item collapsed={collapsed} icon={Wallet2} label="Vendor Advances" to="/accounting/vendor-advances" active={p === '/accounting/vendor-advances'} testid="sidebar-vendor-advances" />
                <Item collapsed={collapsed} icon={ShieldAlert} label="Review Queue" to="/reports/review-queue" active={p === '/reports/review-queue'} testid="sidebar-review-queue" />
                <Item collapsed={collapsed} icon={CalendarClock} label="Tax Calendar" to="/compliance" active={isActive(['/compliance'])} testid="sidebar-compliance" />
              </Section>
            </>
          ) : isHr ? (
            <>
              <Section collapsed={collapsed}>
                <Item collapsed={collapsed} icon={Users} label="Dashboard" to="/hr" active={isActive(['/hr'])} testid="sidebar-hr-dashboard" />
              </Section>
              <Section collapsed={collapsed} label="Team">
                <Item collapsed={collapsed} icon={UserPlus} label="Team Members" to="/users" active={isActive(['/users'])} testid="sidebar-hr-team" />
              </Section>
            </>
          ) : (
            <>
              <Section collapsed={collapsed}>
                <Item collapsed={collapsed} icon={LayoutDashboard} label="Dashboard+" to="/" active={isActive(['/'])} testid="sidebar-dashboard" />
              </Section>

              <Section collapsed={collapsed} label="CRM">
                <Item collapsed={collapsed} icon={UserPlus} label="Leads" to="/leads" active={isActive(['/leads'])} testid="sidebar-leads" />
                <Item collapsed={collapsed} icon={Users} label="Travellers" to="/clients" active={isActive(['/clients'])} testid="sidebar-travellers" />
                <Item collapsed={collapsed} icon={FileText} label="Trips" to="/quotes" active={isActive(['/quotes'])} testid="sidebar-trips" />
                <Item collapsed={collapsed} icon={Package} label="Packages" to="/itineraries" active={isActive(['/itineraries'])} testid="sidebar-packages" />
                <Item collapsed={collapsed} icon={MapPin} label="Destinations" to="/destinations" active={isActive(['/destinations'])} testid="sidebar-destinations" />
              </Section>

              <Section collapsed={collapsed} label="WhatsApp">
                <Item collapsed={collapsed} icon={MessageCircle} label="Chats" to="/whatsapp" active={p === '/whatsapp'} testid="sidebar-whatsapp" />
                {isAdmin && <Item collapsed={collapsed} icon={MessageSquare} label="Message Templates" to="/whatsapp/templates" active={p === '/whatsapp/templates'} testid="sidebar-whatsapp-templates" />}
                {isAdmin && <Item collapsed={collapsed} icon={Zap} label="Automation" to="/whatsapp/automation" active={p === '/whatsapp/automation'} testid="sidebar-whatsapp-automation" />}
              </Section>

              <Section collapsed={collapsed}>
                <Item collapsed={collapsed} icon={LayoutTemplate} label="Templates" to="/templates" active={isActive(['/templates'])} testid="sidebar-templates" />
              </Section>

              <Section collapsed={collapsed} label="Finance">
                <Item
                  collapsed={collapsed}
                  icon={Receipt}
                  label="Vouchers"
                  to="/vouchers"
                  active={isActive(['/vouchers'])}
                  testid="sidebar-vouchers"
                  badge={counts.vouchersMissing ? { count: counts.vouchersMissing, tone: 'red' } : null}
                />
                <Item collapsed={collapsed} icon={FileText} label="Invoices" to="/invoices" active={isActive(['/invoices'])} testid="sidebar-invoices" />
                <Item collapsed={collapsed} icon={IndianRupee} label="Receipts" to="/finance/receipts" active={isActive(['/finance/receipts'])} testid="sidebar-receipts" />
                <Item collapsed={collapsed} icon={BookOpen} label="Ledger" to="/ledger" active={isActive(['/ledger'])} testid="sidebar-ledger" />
                <Item collapsed={collapsed} icon={CreditCard} label="Payment Links" to="/payment-links" active={isActive(['/payment-links'])} testid="sidebar-payment-links" />
                <Item collapsed={collapsed} icon={Building2} label="Vendors" to="/vendors" active={isActive(['/vendors'])} testid="sidebar-vendors" />
                <Item collapsed={collapsed} icon={Undo2} label="Credit Notes" to="/finance/credit-notes" active={isActive(['/finance/credit-notes'])} testid="sidebar-credit-notes" />
                {isAdmin && <Item collapsed={collapsed} icon={HandCoins} label="Reimbursements" to="/finance/reimbursements" active={isActive(['/finance/reimbursements'])} testid="sidebar-reimbursements" />}
              </Section>

              <Section collapsed={collapsed} label="Analytics">
                <Item collapsed={collapsed} icon={LineChart} label="Analytics" to="/analytics" active={isActive(['/analytics'])} testid="sidebar-analytics" />
                {isAdmin && <Item collapsed={collapsed} icon={BarChart3} label="GST Report" to="/reports/gst" active={p === '/reports/gst'} testid="sidebar-gst-report" />}
                {isAdmin && <Item collapsed={collapsed} icon={BarChart3} label="TCS Report" to="/reports/tcs" active={p === '/reports/tcs'} testid="sidebar-tcs-report" />}
                {isAdmin && <Item collapsed={collapsed} icon={History} label="Historical Tax" to="/accounting/gst-tcs/historical" active={p === '/accounting/gst-tcs/historical'} testid="sidebar-historical-tax" />}
                {isAdmin && <Item collapsed={collapsed} icon={ScanSearch} label="Reconciliation" to="/reports/reconciliation" active={p === '/reports/reconciliation'} testid="sidebar-reconciliation-report" />}
                {isAdmin && <Item collapsed={collapsed} icon={PieChart} label="Reconstructed Accounts" to="/reports/reconstructed-accounts" active={p === '/reports/reconstructed-accounts'} testid="sidebar-reconstructed-accounts" />}
                {isAdmin && <Item collapsed={collapsed} icon={Globe} label="FX Reporting" to="/reports/fx" active={p === '/reports/fx'} testid="sidebar-fx-report" />}
                {isAdmin && <Item collapsed={collapsed} icon={FileDown} label="Tally Export" to="/reports/tally-export" active={p === '/reports/tally-export'} testid="sidebar-tally-export" />}
                {isAdmin && <Item collapsed={collapsed} icon={Landmark} label="Bank / Cash Accounts" to="/accounting/bank-accounts" active={p === '/accounting/bank-accounts'} testid="sidebar-bank-accounts" />}
                {isAdmin && <Item collapsed={collapsed} icon={Wallet2} label="Vendor Advances" to="/accounting/vendor-advances" active={p === '/accounting/vendor-advances'} testid="sidebar-vendor-advances" />}
                {isAdmin && <Item collapsed={collapsed} icon={ShieldAlert} label="Review Queue" to="/reports/review-queue" active={p === '/reports/review-queue'} testid="sidebar-review-queue" />}
                {isAdmin && <Item collapsed={collapsed} icon={Cpu} label="AI Usage" to="/admin/ai-usage" active={p === '/admin/ai-usage'} testid="sidebar-ai-usage" />}
                {isAdmin && <Item collapsed={collapsed} icon={AlertTriangle} label="Pricing Audit" to="/admin/pricing-audit" active={p === '/admin/pricing-audit'} testid="sidebar-pricing-audit" />}
                {isAdmin && <Item collapsed={collapsed} icon={Copy} label="Duplicate Detector" to="/admin/orphan-detector" active={p === '/admin/orphan-detector'} testid="sidebar-orphan-detector" />}
              </Section>

              <Section collapsed={collapsed} label="Settings">
                <Item collapsed={collapsed} icon={Globe} label="Website" to="/website" active={isActive(['/website'])} testid="sidebar-website" />
                <Item collapsed={collapsed} icon={Wrench} label="Tools" to="/tools" active={isActive(['/tools'])} testid="sidebar-tools" />
                <Item collapsed={collapsed} icon={Settings} label="Admin & Settings" to="/admin" active={isActive(['/admin', '/settings'])} testid="sidebar-admin-settings" />
              </Section>
            </>
          )}
        </nav>

        <SidebarFooter collapsed={collapsed} user={user} />
      </aside>
    </TooltipProvider>
  );
};

const Section = ({ collapsed, label, children }) => (
  <div>
    {label ? (
      <div
        className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"
        style={{ opacity: collapsed ? 0 : 1, transition: 'opacity 150ms ease', pointerEvents: collapsed ? 'none' : 'auto' }}
      >
        {label}
      </div>
    ) : null}
    <div className="space-y-0.5">{children}</div>
  </div>
);

const Item = ({ collapsed, icon: Icon, label, to, active, testid, badge }) => {
  const link = (
    <Link
      to={to}
      data-testid={testid}
      className="group relative flex items-center gap-2.5 h-9 rounded-lg text-sm transition-colors"
      style={{
        // Consistent 10px gutter on both sides when expanded. When collapsed
        // we center the icon inside the 56-px-wide rail by zeroing the
        // padding so the row layout-shift on toggle is zero.
        paddingLeft: collapsed ? 0 : 10,
        paddingRight: collapsed ? 0 : 10,
        justifyContent: collapsed ? 'center' : 'flex-start',
        backgroundColor: active ? ACTIVE_BG : 'transparent',
        color: active ? PRIMARY : '#475569',
        fontWeight: active ? 500 : 400,
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#F8FAFC'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      {/* Active-state left border accent — 3 px teal stripe so the active
          item reads at a glance even in collapsed mode. */}
      {active ? (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r"
          style={{ backgroundColor: PRIMARY }}
        />
      ) : null}
      <Icon className="h-4 w-4 shrink-0" style={{ opacity: active ? 1 : 0.7 }} />
      {/* Label — hidden completely when collapsed (display:none) so it
          doesn't keep `flex-1` width and push the icon off-center.
          Phase 16.2.1 — the prior `opacity:0` approach still reserved
          space, causing the Vouchers + other badged rows to look shifted. */}
      {!collapsed ? (
        <span
          className="truncate flex-1"
          style={{ opacity: 1, transition: 'opacity 150ms ease' }}
        >
          {label}
        </span>
      ) : null}
      {badge && !collapsed ? (
        <span
          data-testid={testid + '-badge'}
          className="text-[10px] font-semibold rounded-full px-1.5 py-0.5 shrink-0 self-center leading-none"
          style={{
            backgroundColor: badge.tone === 'amber' ? '#FEF3C7' : '#FEE2E2',
            color: badge.tone === 'amber' ? '#B45309' : '#B91C1C',
            marginLeft: 'auto',
            minWidth: '18px',
            textAlign: 'center',
          }}
        >
          {badge.count}
        </span>
      ) : null}
      {/* Collapsed-state badge — tiny dot on the icon corner so the user
          still gets the "something needs attention" signal without
          warping the centered icon row. */}
      {badge && collapsed ? (
        <span
          data-testid={testid + '-badge-dot'}
          aria-label={`${badge.count} ${label} need attention`}
          className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full shrink-0"
          style={{ backgroundColor: badge.tone === 'amber' ? '#F59E0B' : '#DC2626' }}
        />
      ) : null}
    </Link>
  );

  if (!collapsed) return link;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={6}>
        <span style={{ fontFamily: FONT }}>{label}</span>
        {badge ? <span className="ml-1.5 text-xs opacity-75">({badge.count})</span> : null}
      </TooltipContent>
    </Tooltip>
  );
};

const SidebarFooter = ({ collapsed, user }) => {
  const initial = ((user && (user.name || user.email)) || '?')[0].toUpperCase();
  const name = (user && user.name) || (user && user.email) || 'User';
  const role = (user && user.role) || 'member';

  const footer = (
    <Link
      to="/settings"
      data-testid="sidebar-footer"
      className="flex items-center gap-2.5 px-2.5 py-2 mx-2 mb-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
      style={{ justifyContent: collapsed ? 'center' : 'flex-start' }}
    >
      <div
        className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0"
        style={{ backgroundColor: PRIMARY }}
      >
        {initial}
      </div>
      <div
        className="flex-1 min-w-0"
        style={{ opacity: collapsed ? 0 : 1, transition: 'opacity 150ms ease', pointerEvents: collapsed ? 'none' : 'auto' }}
      >
        <div className="text-xs font-semibold text-slate-900 truncate">{name}</div>
        <div className="text-[10px] text-slate-400 uppercase tracking-wide truncate">{role}</div>
      </div>
    </Link>
  );

  if (!collapsed) return footer;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{footer}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={6}>
        <span style={{ fontFamily: FONT }}>{name} · {role}</span>
      </TooltipContent>
    </Tooltip>
  );
};

export default Sidebar;
